import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import { AskRequest, User } from '@hone-automation/common';
import { prefixClass } from 'lib/utils';
import { IconFolder, IconChevronDown } from 'components/Icons';
import { Tooltip } from '../../../../Common';
import { DraggableRequestCard } from './DraggableRequestCard';
import { formatMessageContentToPlainText } from '../../../../Cards/MessagesCard/subcomponents/MessageContent/formatMessageContent';
import type { RequestCardAssignee, WorkState } from './RequestCard';
import type { FilterView } from '../../../../../Queue/QueueContext';
import type { DateField, DateGroupKey } from '../../utils/dateGrouping';
import './CollapsibleGroupCard.scss';

export interface CollapsibleGroupCardProps {
  /** The group request object */
  group: AskRequest;
  /** All requests (to find children) */
  allRequests: AskRequest[];
  /** Current active view for contextual filtering */
  activeView: FilterView | undefined;
  /** Current user ID for view filtering */
  userId: string | null | undefined;
  /** Whether this card is the focused/active item (for keyboard nav) */
  isFocused?: boolean;
  /** Whether this card is in the current selection set */
  isSelected?: boolean;
  /** Whether multi-select mode is active (passed to child cards) */
  isMultiSelectMode?: boolean;
  /** Set of currently selected IDs */
  selectedIds: Set<string>;
  /** Currently focused item ID */
  focusedId: string | null;
  /** Filter function from toolbar filters */
  filterRequests: (requests: AskRequest[], options?: { skipGroupFilter?: boolean }) => AskRequest[];
  /** Date field used for grouping */
  dateField: DateField;
  /** Date group key for formatting */
  groupKey: DateGroupKey;
  /** Format date label for card display */
  formatCardDateLabel: (date: Date, groupKey: DateGroupKey) => string;
  /** Format full date/time for tooltip */
  formatFullDateTime: (date: Date) => string;
  /** Click handler for the group header */
  onClick?: (e: React.MouseEvent) => void;
  /** Click handler for child items */
  onChildClick: (childId: string, e: React.MouseEvent) => void;
  /** Checkbox change handler for child items */
  onChildCheckChange: (childId: string, checked: boolean) => void;
  /** Users for RequestCard avatars */
  requestCardUsers?: User[];
  /** Check if request has mention */
  checkHasMention: (request: AskRequest) => boolean;
  /** Check if request has attachment */
  checkHasAttachment: (request: AskRequest) => boolean;
  /** Get work state for request */
  checkGetWorkState: (request: AskRequest) => WorkState;
  /** Get assignee info for request */
  getAssignee: (request: AskRequest) => RequestCardAssignee | undefined;
  /** Check if request is internal */
  isInternal: (request: AskRequest) => boolean;
  /** Search query for highlighting */
  highlightQuery?: string;
  /** Get date for field */
  getDateForField: (request: AskRequest, field: DateField) => Date | null;
  /** Get last activity date */
  getLastActivityDate: (request: AskRequest) => Date;
  /** Date field labels for tooltip */
  dateFieldLabels: Record<DateField, string>;
  /** Parse local date string */
  parseLocalDate: (dateStr: string) => Date;
  /** Get short ID for display */
  getShortId: (id: string) => string;
  /** Whether drag is disabled (e.g., in closed view) */
  dragDisabled?: boolean;
  /** Total child count from group summary (independent of view filtering) */
  totalGroupChildCount?: number;
}

/**
 * CollapsibleGroupCard - A collapsible container that renders child RequestCards nested inside.
 * Shows contextually filtered children based on the current view (Me, Team, Client, etc.).
 * Returns null if no children match the current filters.
 */
export const CollapsibleGroupCard: React.FC<CollapsibleGroupCardProps> = ({
  group,
  allRequests,
  activeView,
  userId,
  isFocused = false,
  isSelected = false,
  isMultiSelectMode = false,
  selectedIds,
  focusedId,
  filterRequests,
  dateField,
  groupKey,
  formatCardDateLabel,
  formatFullDateTime,
  onClick,
  onChildClick,
  onChildCheckChange,
  requestCardUsers,
  checkHasMention,
  checkHasAttachment,
  checkGetWorkState,
  getAssignee,
  isInternal,
  highlightQuery,
  getDateForField,
  getLastActivityDate,
  dateFieldLabels,
  parseLocalDate,
  getShortId,
  dragDisabled = false,
  totalGroupChildCount,
}) => {
  const prefix = prefixClass('ksq2-collapsible-group-card');
  const [isExpanded, setIsExpanded] = useState(false);

  // Get all children of this group
  const allChildren = useMemo(() => {
    return allRequests.filter(r => r.parentId === group.id && r.type !== 'Group');
  }, [allRequests, group.id]);

  // Apply view filtering to children based on activeView
  // This ensures children match the same criteria as top-level requests in each view
  const viewFilteredChildren = useMemo(() => {
    if (!activeView) return allChildren;

    switch (activeView) {
      case 'Me':
        return allChildren.filter(r => r.priority !== 'Blocker' && r.status === 'Open' && r.assignee === userId);
      case 'My Team':
        return allChildren.filter(r => r.priority !== 'Blocker' && r.status === 'Open' && r.assignee !== userId);
      case 'Client':
        return allChildren.filter(r => r.priority !== 'Blocker' && r.status === 'In Progress');
      case 'Completed':
        return allChildren.filter(r => r.priority !== 'Blocker' && r.status === 'Closed');
      case 'Blockers':
        return allChildren.filter(r => r.priority === 'Blocker');
      case 'Groups':
        return allChildren; // Show all children when viewing groups
      default:
        return allChildren;
    }
  }, [allChildren, activeView, userId]);

  // Apply toolbar filters but skip groupFilterActive (children are never groups)
  const filteredChildren = useMemo(() => {
    return filterRequests(viewFilteredChildren, { skipGroupFilter: true });
  }, [filterRequests, viewFilteredChildren]);

  // Calculate total children for display
  const totalChildCount = viewFilteredChildren.length;
  const visibleChildCount = filteredChildren.length;

  // Hide group if no children match the current view (except in Groups view, always show)
  if (activeView !== 'Groups' && visibleChildCount === 0) {
    return null;
  }

  const handleLeftColumnClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(prev => !prev);
  };

  const handleHeaderClick = (e: React.MouseEvent) => {
    // If clicking on the left column (chevron + folder), just toggle expand
    const target = e.target as HTMLElement;
    if (target.closest(`.${prefix('__leftColumn')}`)) {
      return;
    }
    onClick?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(e as unknown as React.MouseEvent);
    }
    if (e.key === 'ArrowRight' && !isExpanded) {
      e.preventDefault();
      setIsExpanded(true);
    }
    if (e.key === 'ArrowLeft' && isExpanded) {
      e.preventDefault();
      setIsExpanded(false);
    }
  };

  // Get date for display
  const fieldDate = getDateForField(group, dateField);
  const displayDate = fieldDate || getLastActivityDate(group);

  // Count display: "3" when all visible, "1/3" when filtered
  // Use totalGroupChildCount (from group summary) as the true total if available
  const trueTotal = totalGroupChildCount ?? totalChildCount;
  const isCountFiltered = visibleChildCount !== trueTotal;
  const countText = isCountFiltered ? `${visibleChildCount}/${trueTotal}` : `${trueTotal}`;

  // Creator info from first comment
  const creatorInfo = useMemo(() => {
    const firstComment = group.comments?.[0];
    if (!firstComment?.userLocation) return undefined;
    const ul = firstComment.userLocation;
    // @ts-ignore - name may exist at runtime
    const name = firstComment.user?.name || ul.username || ul.email?.split('@')[0];
    const isInternalUser = firstComment.user?.isInternalUser || ul.isHoneInternalUser;
    const type = isInternalUser ? 'ks' : 'client';
    return { name, type: type as 'ks' | 'client', id: ul.userId || '' };
  }, [group.comments]);

  // Memo: first meaningful comment text
  const memoText = useMemo(() => {
    if (!group.comments || group.comments.length === 0) return undefined;
    // Skip "Group created" default comment
    for (const c of group.comments) {
      const text = c.comment;
      if (!text) continue;
      const lower = text.toLowerCase().trim();
      if (lower === 'group created' || lower === 'group created.') continue;
      return formatMessageContentToPlainText(text);
    }
    return undefined;
  }, [group.comments]);

  return (
    <div
      className={classNames(prefix(), {
        [prefix('--focused')]: isFocused,
        [prefix('--selected')]: isSelected,
        [prefix('--expanded')]: isExpanded,
      })}
      data-request-id={group.id}
    >
      {/* Header */}
      <div
        className={prefix('__header')}
        onClick={handleHeaderClick}
        onKeyDown={handleKeyDown}
        role="option"
        aria-selected={isSelected}
        aria-expanded={isExpanded}
        tabIndex={isFocused ? 0 : -1}
      >
        <div
          className={prefix('__leftColumn')}
          onClick={handleLeftColumnClick}
          role="button"
          aria-label={isExpanded ? 'Collapse group' : 'Expand group'}
        >
          <div
            className={classNames(prefix('__chevron'), {
              [prefix('__chevron--expanded')]: isExpanded,
            })}
          >
            <IconChevronDown />
          </div>
          <div className={prefix('__folder-icon')}>
            <IconFolder />
          </div>
        </div>

        <Tooltip
          content={
            <div>
              <div>{group.title || 'Untitled Group'}</div>
              {creatorInfo && (
                <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>Created by {creatorInfo.name}</div>
              )}
            </div>
          }
          showDelay={800}
        >
          <div className={prefix('__content')}>
            <div className={prefix('__top-row')}>
              <div className={prefix('__title')}>{group.title || 'Untitled Group'}</div>
              <div className={prefix('__top-right')}>
                {fieldDate ? (
                  <Tooltip content={formatFullDateTime(displayDate)} showDelay={800}>
                    <span className={prefix('__date')}>{formatCardDateLabel(displayDate, groupKey)}</span>
                  </Tooltip>
                ) : (
                  <Tooltip content={`No ${dateFieldLabels[dateField]}`} showDelay={800}>
                    <span className={prefix('__date')}>—</span>
                  </Tooltip>
                )}
                <span className={prefix('__top-separator')}>·</span>
                <span
                  className={classNames(prefix('__child-count-pill'), {
                    [prefix('__child-count-pill--circle')]: !isCountFiltered,
                  })}
                >
                  {countText}
                </span>
              </div>
            </div>
            {memoText && <div className={prefix('__memo')}>{memoText}</div>}
          </div>
        </Tooltip>
      </div>

      {/* Children container */}
      {isExpanded && (
        <div className={prefix('__children')}>
          {filteredChildren.map(request => {
            const childFieldDate = getDateForField(request, dateField);
            const childDisplayDate = childFieldDate || getLastActivityDate(request);

            return (
              <DraggableRequestCard
                key={request.id}
                id={request.id}
                title={request.title || request.identifier || `Request #${getShortId(request.id)}`}
                date={childFieldDate ? formatCardDateLabel(childDisplayDate, groupKey) : '—'}
                dateTooltip={childFieldDate ? formatFullDateTime(childDisplayDate) : `No ${dateFieldLabels[dateField]}`}
                dueDate={request.dueDate ? parseLocalDate(request.dueDate) : undefined}
                assignee={getAssignee(request)}
                requesterName={request.comments?.[0]?.userLocation?.email?.split('@')[0]}
                memo={request.comments?.[request.comments.length - 1]?.comment}
                users={requestCardUsers}
                // @ts-ignore - flaggedAmount and transaction.amount may exist
                amount={request.flaggedAmount ? parseFloat(request.flaggedAmount) : request.transaction?.amount}
                workState={checkGetWorkState(request)}
                hasMention={checkHasMention(request)}
                hasAttachment={checkHasAttachment(request)}
                isInternal={isInternal(request)}
                isFocused={focusedId === request.id}
                isSelected={selectedIds.has(request.id)}
                isMultiSelectMode={isMultiSelectMode}
                searchQuery={highlightQuery}
                selectedIds={Array.from(selectedIds)}
                dragDisabled={dragDisabled}
                onClick={e => onChildClick(request.id, e)}
                onCheckChange={checked => onChildCheckChange(request.id, checked)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
